import { Component, ChangeDetectionStrategy, input, signal, computed, inject, effect } from '@angular/core';
import { Router, RouterModule, NavigationEnd, ActivatedRoute, isActive, IsActiveMatchOptions } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { NavItem } from './sidebar-nav.types';
import { ZagIcon } from '../icon/icon';

/**
 * Navigation item component.
 *
 * Renders a single navigation item. Supports:
 * - Active route detection (with returnUrl awareness for access-denied pages)
 * - "Coming soon" disabled state with badge
 * - Collapsed (icon-only) vs expanded label display
 */
@Component({
  selector: 'zag-sidebar-nav-item',
  imports: [RouterModule, TranslatePipe, ZagIcon],
  template: `
    <li class="zag-sidebar-nav-item">
      @if (item().comingSoon) {
        <!-- Coming soon item (disabled, no navigation) -->
        <span
          class="zag-sidebar-nav-item__link zag-sidebar-nav-item__link--disabled"
          [attr.aria-disabled]="true"
          [attr.aria-label]="item().label | translate"
        >
          <span [zagIcon]="item().icon" class="zag-sidebar-nav-item__icon"></span>
          @if (!collapsed()) {
            <span class="zag-sidebar-nav-item__label">{{ item().label | translate }}</span>
            <span class="zag-sidebar-nav-item__badge zag-sidebar-nav-item__badge--coming-soon">
              {{ 'common.comingSoon' | translate }}
            </span>
          }
        </span>
      } @else {
        <!-- Route link -->
        <a
          [routerLink]="item().route"
          class="zag-sidebar-nav-item__link"
          [class.zag-sidebar-nav-item__link--active]="isActive()"
          [attr.aria-current]="isActive() ? 'page' : null"
          [attr.aria-label]="item().label | translate"
        >
          <span [zagIcon]="item().icon" class="zag-sidebar-nav-item__icon"></span>
          @if (!collapsed()) {
            <span class="zag-sidebar-nav-item__label">{{ item().label | translate }}</span>
          }
          @if (item().badge && !collapsed()) {
            <span class="zag-sidebar-nav-item__badge">{{ item().badge }}</span>
          }
        </a>
      }
    </li>
  `,
  styleUrl: './sidebar-nav-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarNavItemComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Inputs
  readonly item = input.required<NavItem>();
  readonly collapsed = input<boolean>(false);

  // Track current URL to make isActive reactive to navigation
  private readonly _currentUrl = signal<string>(this.router.url);

  // Track returnUrl query param for highlighting intended destination
  private readonly _returnUrl = signal<string | null>(null);

  // Computed: Is this item active?
  readonly isActive = computed(() => {
    // Reference currentUrl and returnUrl to create reactive dependencies
    this._currentUrl();
    const returnUrl = this._returnUrl();
    return this.checkIfActive(this.item(), returnUrl);
  });

  constructor() {
    // Update currentUrl signal on navigation
    effect(() => {
      this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(event => {
        this._currentUrl.set(event.urlAfterRedirects);
      });
    });

    // Track returnUrl query param changes
    effect(() => {
      this.route.queryParams.subscribe(params => {
        this._returnUrl.set(params['returnUrl'] ?? null);
      });
    });
  }

  /**
   * Check if this item is active.
   *
   * Active detection considers:
   * 1. Current route match (takes precedence)
   * 2. returnUrl query param match (for access denied pages)
   */
  private static readonly ACTIVE_MATCH_OPTIONS: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  private checkIfActive(item: NavItem, returnUrl: string | null): boolean {
    if (!item.route) {
      return false;
    }

    // Check if current route is active
    const isCurrentRoute = isActive(item.route, this.router, SidebarNavItemComponent.ACTIVE_MATCH_OPTIONS);
    if (isCurrentRoute()) {
      return true;
    }

    // Check if returnUrl matches this item's route (for access denied pages)
    if (returnUrl && this.matchesReturnUrl(item.route, returnUrl)) {
      return true;
    }

    return false;
  }

  /**
   * Check if a route matches the returnUrl using subset matching.
   */
  private matchesReturnUrl(route: string, returnUrl: string): boolean {
    const normalizedRoute = route.replace(/^\//, '').split('?')[0];
    const normalizedReturnUrl = returnUrl.replace(/^\//, '').split('?')[0];
    return normalizedReturnUrl === normalizedRoute || normalizedReturnUrl.startsWith(normalizedRoute + '/');
  }
}
